import React, { useEffect, useMemo, useState } from "react";
import DataProvider from "../../services";
import { Image, Segmented, Select } from "antd";
import {
  FileProtectOutlined,
  MedicineBoxOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Loader from "../cards/loader";

interface ClaimRecord {
  id: number;
  type: string;
  deathCertificate?: string;
  nationalId?: string;
  policeReport?: string;
  affidavits?: string;
  excuseDuty?: string;
  dischargeslip?: string;
  hospitalInvoice?: string;
  notes?: string;
  status: string;
  reason?: string;
  createdAt: Date;
}

const UPLOAD_FIELDS: { key: keyof ClaimRecord; label: string }[] = [
  { key: "deathCertificate", label: "Death certificate" },
  { key: "policeReport", label: "Police report" },
  { key: "affidavits", label: "Affidavits" },
  { key: "hospitalInvoice", label: "Hospital invoice" },
  { key: "dischargeslip", label: "Discharge slip" },
  { key: "excuseDuty", label: "Excuse duty" },
  { key: "nationalId", label: "National ID" },
];

function StatusBadge({ status }: { status: string }) {
  const s = (status || "").toLowerCase();
  const config =
    s === "approved"
      ? {
          className: "bg-emerald-50 text-emerald-700 border-emerald-200",
          label: "Approved",
        }
      : s === "rejected"
        ? {
            className: "bg-red-50 text-red-700 border-red-200",
            label: "Rejected",
          }
        : {
            className: "bg-amber-50 text-amber-700 border-amber-200",
            label: "Pending",
          };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border shrink-0 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

export default function DeathClaimsT() {
  const [data, setData] = useState<ClaimRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<string>("All");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  async function getClaims() {
    try {
      setLoading(true);
      const { data } = await DataProvider.getClaims();
      if (data) {
        setData([...data].reverse());
      }
    } catch (error) {
      // Keep whatever was already loaded rather than clearing it on a
      // transient fetch failure.
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getClaims();
  }, []);

  const filtered = useMemo(() => {
    return data.filter((record) => {
      const matchesType =
        typeFilter === "All" ||
        record.type?.toLowerCase().startsWith(typeFilter.toLowerCase());
      const matchesStatus =
        statusFilter === "all" ||
        record.status?.toLowerCase() === statusFilter;
      return matchesType && matchesStatus;
    });
  }, [data, typeFilter, statusFilter]);

  if (loading && data.length === 0) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader num={1} />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-5 py-4 border-b border-neutral-100">
        <Segmented
          value={typeFilter}
          onChange={(v) => setTypeFilter(v as string)}
          options={["All", "Death", "Hospitalized"]}
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          className="w-full sm:w-40"
          options={[
            { value: "all", label: "All statuses" },
            { value: "pending", label: "Pending" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
          ]}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <div className="h-14 w-14 rounded-full bg-neutral-100 flex items-center justify-center mb-3">
            <InboxOutlined className="text-2xl text-neutral-400" />
          </div>
          <p className="text-neutral-600 font-medium mb-1">
            {data.length === 0
              ? "No claims yet"
              : "No claims match these filters"}
          </p>
          <p className="text-sm text-neutral-500">
            {data.length === 0
              ? "Claims you request will show up here."
              : "Try a different type or status."}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-neutral-100">
          {filtered.map((record) => {
            const uploads = UPLOAD_FIELDS.filter(
              (f) => typeof record[f.key] === "string" && record[f.key],
            );
            const isDeath = record.type?.toLowerCase().startsWith("death");
            return (
              <div key={record.id} className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                        isDeath
                          ? "bg-neutral-100 text-neutral-600"
                          : "bg-[#06509E]/10 text-[#06509E]"
                      }`}
                      aria-hidden
                    >
                      {isDeath ? (
                        <FileProtectOutlined />
                      ) : (
                        <MedicineBoxOutlined />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-neutral-900 capitalize truncate">
                        {record.type}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {dayjs(record.createdAt).format("Do MMMM YYYY")}
                      </p>
                    </div>
                  </div>
                  <StatusBadge status={record.status} />
                </div>

                {record.reason && (
                  <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {record.reason}
                  </p>
                )}

                {uploads.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-neutral-500 mb-2">
                      {uploads.length} document
                      {uploads.length > 1 ? "s" : ""} attached
                    </p>
                    <Image.PreviewGroup>
                      <div className="flex flex-wrap gap-2">
                        {uploads.map((f) => (
                          <Image
                            key={String(f.key)}
                            src={record[f.key] as string}
                            alt={f.label}
                            width={56}
                            height={56}
                            className="rounded-lg object-cover border border-neutral-200"
                            style={{ objectFit: "cover" }}
                          />
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
